const isLocalStorageSupported = (() => {
  const mod = 'test_local_storage'

  try {
    localStorage.setItem(mod, mod);
    localStorage.removeItem(mod);
    return true;
  } catch(e) {
    return false;
  }
})()

const STRATEGY = {
  LOCAL: 'LOCAL',
  SESSION: 'SESSION',
  MEMORY: 'MEMORY'
}

const setToStorage = (storage, key, value) => {
  if (isLocalStorageSupported) {
    storage.setItem(key, JSON.stringify(value))
  }
}

const getFromStorage = (storage, key) => {
  if (isLocalStorageSupported) {
    const result = storage.getItem(key)

    if (!result) {
      return undefined
    }

    try {
      return JSON.parse(result)
    } catch (ex) {
      return undefined
    }
  }

  return undefined
}

const removeFromStorage = (storage, key) => {
  storage.removeItem(key)
}

/* --------------------------------- */

const isBrowser = typeof window !== 'undefined'

const createStorageMethods = method => {
  if (isBrowser && method === STRATEGY.LOCAL) {
    return {
      get: (...args) => getFromStorage(localStorage, ...args),
      set: (...args) => setToStorage(localStorage, ...args),
      remove: (...args) => removeFromStorage(localStorage, ...args)
    }
  } else if (isBrowser && method === STRATEGY.SESSION) {
    return {
      get: (...args) => getFromStorage(sessionStorage, ...args),
      set: (...args) => setToStorage(sessionStorage, ...args),
      remove: (...args) => removeFromStorage(sessionStorage, ...args)
    }
  } else {
    // default have memory cache
    return {
      get: () => {},
      set: () => {},
      remove: () => {}
    }
  }
}

/* --------------------------------- */

const createStorage = (key, { expire, strategy = STRATEGY.LOCAL } = {}) => {
  let cache = undefined

  if (!key) {
    throw new Error('must provide key to create storage')
  }

  const expireKey = `${key}_expire`
  const methods = createStorageMethods(strategy)

  const clear = () => {
    cache = undefined
    methods.remove(key)
    if (expire) {
      methods.remove(expireKey)
    }
    return cache
  }

  const getItem = defaultValue => {
    if (cache === undefined) {
      cache = methods.get(key)
    }

    if (cache !== undefined && expire) {
      const expireDate = methods.get(expireKey)
      if (expireDate && !isNaN(expireDate) && Date.now() > expireDate) {
        clear()
      }
    }

    if (cache === undefined && defaultValue !== undefined) {
      cache = setItem(defaultValue)
    }

    return cache
  }

  const refreshItem = () => {
    cache = undefined
    return getItem()
  }

  const setItem = value => {
    methods.set(key, value)
    if (expire) {
      methods.set(expireKey, Date.now() + expire)
    }

    cache = value

    return cache
  }


  const getItemAsync = fallback => {
    let result = getItem()

    if (result === undefined) {
      if (typeof fallback === 'function') {
        result = fallback()
      } else if (fallback !== undefined) {
        result = fallback
      }

      return Promise.resolve(result).then(
        value => {
          setItem(value)
          return value
        },
        err => {
          clear()
          throw err
        }
      )

    } else {
      return Promise.resolve(result)
    }
  }


  return {
    getItem,
    setItem,
    getItemAsync,
    clear,
    clearItem: clear,
    refreshItem,
  }
}

export default createStorage
